import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import gsap from 'gsap'
import { Globe, ShieldCheck, CheckCircle2, AlertTriangle, Circle, Eye, MousePointerClick, MailWarning, Clock, type LucideProps } from 'lucide-react'
import { api, relTime, statusBadge, type Me } from '../lib/api'
import { useCountUp, useGsap } from '../lib/useGsap'
import { RateCard, StackedBars } from '../components/Chart'

type Tone = 'brand' | 'ok' | 'warn'
function Kpi({ label, value, to, icon, tone = 'brand', sub }: {
  label: string; value: number; to?: string; icon: React.ReactNode; tone?: Tone; sub?: string
}) {
  const ref = useCountUp(value)
  const body = (
    <>
      <span className={`kpi-ico ${tone}`}>{icon}</span>
      <div className="kpi-body">
        <span className="kpi-value" ref={ref}>{value}</span>
        <span className="kpi-label">{label}</span>
        {sub && <span className="kpi-sub">{sub}</span>}
      </div>
    </>
  )
  return to
    ? <Link to={to} className="kpi linked">{body}</Link>
    : <div className="kpi">{body}</div>
}
const ip: LucideProps = { size: 17, strokeWidth: 2 }

/**
 * First-run checklist. Shows the four things a new account must do to send real mail, with
 * each step ticked as it's completed. Hides itself once everything is done, so an established
 * account never sees it.
 */
function GettingStarted({ me, hasSent }: { me: Me; hasSent: boolean }) {
  const steps = [
    { done: me.counts.domains > 0, label: 'Add your sending domain', hint: 'The domain your mail comes from.', to: '/domains', cta: 'Add domain' },
    { done: me.counts.verified_domains > 0, label: 'Publish DNS & verify', hint: 'SPF, DKIM and DMARC — we generate them, you publish.', to: '/domains', cta: 'Verify' },
    { done: me.counts.api_keys > 0, label: 'Create an API key', hint: 'To send through the API or SMTP.', to: '/api-keys', cta: 'Create key' },
    { done: hasSent, label: 'Send your first message', hint: 'From the dashboard or the API.', to: '/send', cta: 'Send' },
  ]
  const doneCount = steps.filter(s => s.done).length
  if (doneCount === steps.length) return null
  const next = steps.find(s => !s.done)

  return (
    <div className="panel" style={{ marginBottom: 22 }}>
      <div className="panel-head">
        <h2>Getting started</h2>
        <span className="small muted">{doneCount} of {steps.length} done</span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {steps.map((s, i) => {
          const isNext = s === next
          return (
            <div key={i} className="row" style={{ gap: 12, alignItems: 'center', padding: '10px 4px',
              borderBottom: i < steps.length - 1 ? '1px solid var(--border)' : 'none' }}>
              <span style={{ color: s.done ? 'var(--success)' : isNext ? 'var(--brand-600)' : 'var(--text-muted)', display: 'flex' }}>
                {s.done ? <CheckCircle2 size={20} /> : <Circle size={20} />}
              </span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, color: s.done ? 'var(--text-muted)' : 'var(--text)',
                  textDecoration: s.done ? 'line-through' : 'none' }}>{s.label}</div>
                <div className="small muted">{s.hint}</div>
              </div>
              {!s.done && isNext && <Link to={s.to} className="btn-solid" style={{ padding: '7px 14px', fontSize: 13 }}>{s.cta} →</Link>}
            </div>
          )
        })}
      </div>
    </div>
  )
}

export function Overview({ me }: { me: Me }) {
  const { data: domains } = useQuery({ queryKey: ['domains'], queryFn: api.domains.list })
  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ['stats', 30],
    queryFn: () => api.stats.get(30),
  })

  const ref = useGsap(({ el }) => {
    gsap.from(el.querySelectorAll('.stat'), {
      y: 12, opacity: 0, duration: 0.4, stagger: 0.05, ease: 'power2.out',
    })
  })

  const unverified = domains?.data.filter(d => !d.can_send).length ?? 0
  const t = stats?.totals
  const th = stats?.thresholds

  // Short labels: 30 full dates on an axis is unreadable, and the tooltip carries the
  // precise value anyway.
  const labels = stats?.daily.map(d =>
    new Date(d.date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })) ?? []

  return (
    <div ref={ref} className="overview">
      <div className="page-head">
        <div>
          <h1>Overview</h1>
          <p className="lede" style={{ margin: 0 }}>{me.tenant.name}</p>
        </div>
        <Link to="/send" className="btn-solid page-head-cta">Send a message</Link>
      </div>

      {/* Until stats resolve, show one skeleton for the whole KPI + getting-started block. Rendering
          it early painted 0s and flashed "Send your first message" to accounts that have sent
          hundreds — a jarring wrong state that corrected itself only on the next load. */}
      {statsLoading ? (
        <div className="skeleton" style={{ height: 250, marginBottom: 22, borderRadius: 16 }} />
      ) : (
      <>
      <GettingStarted me={me} hasSent={(t?.delivered ?? 0) + (t?.accepted ?? 0) > 0 || (stats?.recent_messages.length ?? 0) > 0} />

      <div className="kpis">
        <Kpi label="Domains" value={me.counts.domains} to="/domains" tone="brand"
             icon={<Globe {...ip} />} sub="sending domains" />
        <Kpi label="Verified" value={me.counts.verified_domains} to="/domains" tone="ok"
             icon={<ShieldCheck {...ip} />} sub="ready to send" />
        <Kpi label="Delivered · 30d" value={t?.delivered ?? 0} to="/messages" tone="ok"
             icon={<CheckCircle2 {...ip} />} sub="accepted by receivers" />
        <Kpi label="Bounced · 30d" value={t?.bounced ?? 0} to="/messages" tone="warn"
             icon={<AlertTriangle {...ip} />} sub="rejected or undeliverable" />
        <Kpi label="Opened · 30d" value={t?.opened ?? 0} to="/messages" tone="brand"
             icon={<Eye {...ip} />} sub="recipients who opened" />
        <Kpi label="Clicked · 30d" value={t?.clicked ?? 0} to="/messages" tone="brand"
             icon={<MousePointerClick {...ip} />} sub="tracked-link clicks" />
        <Kpi label="Complaints · 30d" value={t?.complained ?? 0} to="/messages" tone="warn"
             icon={<MailWarning {...ip} />} sub="marked as spam" />
        <Kpi label="Deferred · 30d" value={t?.deferred ?? 0} to="/messages" tone="warn"
             icon={<Clock {...ip} />} sub="temporary, retrying" />
      </div>
      </>
      )}

      {unverified > 0 && (
        <div className="alert warn">
          <strong>{unverified} domain{unverified > 1 ? 's' : ''} not verified yet.</strong>{' '}
          <Link to="/domains">Finish the DNS setup</Link> to start sending from them.
        </div>
      )}

      <div className="panel">
        <div className="panel-head"><h2>Deliverability</h2></div>
        <p className="small muted" style={{ margin: '0 0 14px' }}>
          These three numbers decide whether your mail reaches inboxes. Gmail and Yahoo act on
          complaint rate above 0.3%, and they do it at the domain level — so one bad campaign
          affects everything you send.
        </p>
        <div className="rates">
          <RateCard
            label="Delivery rate" value={stats?.rates.delivery ?? null}
            warn={95} critical={90}
            hint="Of mail we attempted, the share receivers accepted."
          />
          <RateCard
            label="Bounce rate" value={stats?.rates.bounce ?? null}
            warn={th?.bounce_warning ?? 5} critical={th?.bounce_critical ?? 10} invert
            hint="Above 5% suggests list hygiene problems. Above 10% gets you throttled."
          />
          <RateCard
            label="Complaint rate" value={stats?.rates.complaint ?? null}
            warn={th?.complaint_warning ?? 0.1} critical={th?.complaint_critical ?? 0.3} invert
            hint="Keep below 0.1%. At 0.3% Gmail and Yahoo start rejecting."
          />
        </div>
      </div>

      <div className="dash-cols">
        <div className="dash-col">
          <div className="panel">
            <div className="panel-head"><h2>Last 30 days</h2></div>
            {statsLoading
              ? <div className="skeleton" style={{ height: 190 }} />
              : (
                <StackedBars
                  labels={labels}
                  series={[
                    { key: 'delivered', label: 'Delivered', colour: 'var(--success)', values: stats?.daily.map(d => d.delivered) ?? [] },
                    { key: 'deferred', label: 'Deferred', colour: 'var(--warning)', values: stats?.daily.map(d => d.deferred) ?? [] },
                    { key: 'bounced', label: 'Bounced', colour: 'var(--danger)', values: stats?.daily.map(d => d.bounced) ?? [] },
                  ]}
                />
              )}
            {t && (t.opened > 0 || t.clicked > 0) && (
              <div className="mini-stats">
                <div><span className="k">Opened</span><span className="v">{t.opened.toLocaleString()}</span></div>
                <div><span className="k">Clicked</span><span className="v">{t.clicked.toLocaleString()}</span></div>
                <div><span className="k">Deferred</span><span className="v">{t.deferred.toLocaleString()}</span></div>
                <div><span className="k">Suppressed</span><span className="v">{t.suppressed.toLocaleString()}</span></div>
              </div>
            )}
          </div>

          <div className="panel flush">
            <div className="panel-head" style={{ padding: '16px 18px' }}><h2>Plan &amp; limits</h2></div>
            <table className="kv">
              <tbody>
                <tr><td>Plan</td><td><strong>{me.plan?.name ?? '—'}</strong></td></tr>
                <tr><td>Messages per day</td><td>{me.plan?.daily_send_limit.toLocaleString() ?? '—'}</td></tr>
                <tr><td>Messages per hour</td><td>{me.plan?.hourly_send_limit.toLocaleString() ?? '—'}</td></tr>
                <tr><td>Domains</td><td>{me.counts.domains} <span className="muted">· unlimited</span></td></tr>
                <tr><td>Dedicated IP</td><td>{me.plan?.dedicated_ip_allowed ? 'Available' : 'Not on this plan'}</td></tr>
              </tbody>
            </table>
          </div>
        </div>

        <div className="dash-col">
          <div className="panel flush">
            <div className="panel-head" style={{ padding: '16px 18px' }}>
              <h2>Recent messages</h2>
              {!!stats?.recent_messages.length && <Link to="/messages" className="small">View all →</Link>}
            </div>
            {stats?.recent_messages.length
              ? (
                <div>
                  {stats.recent_messages.map(m => {
                    const s = statusBadge(m.status)
                    return (
                      <Link key={m.id} to={`/messages/${m.id}`} className="list-row">
                        <div style={{ minWidth: 0 }}>
                          <div className="name truncate">{m.subject || '(no subject)'}</div>
                          <div className="small muted truncate">
                            {m.from_address} · {m.recipient_count} recipient
                            {m.recipient_count === 1 ? '' : 's'} · {relTime(m.created_at)}
                          </div>
                        </div>
                        <span className={`badge ${s.cls}`}>{s.text}</span>
                      </Link>
                    )
                  })}
                </div>
              )
              : (
                <div className="empty" style={{ border: 0 }}>
                  <div className="big">✉</div>
                  <p><strong>No messages yet</strong></p>
                  <p>Once a domain is verified, send your first message and it appears here.</p>
                </div>
              )}
          </div>

          <div className="panel ramp-note">
            <div className="panel-head"><h2>Warm-up</h2></div>
            <p className="small muted" style={{ margin: 0 }}>
              New accounts ramp up over their first 30 days regardless of plan limits. Receivers
              build trust in a sending pattern gradually, and going from zero to full volume in a
              day is what gets an IP blocked.
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
